import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(v || 0);

const MONTHS_SW = ['Januari','Februari','Machi','Aprili','Mei','Juni',
                   'Julai','Agosti','Septemba','Oktoba','Novemba','Desemba'];

function monthName(ym) {
  const [y, m] = ym.split('-');
  return `${MONTHS_SW[+m - 1]} ${y}`;
}

function generateMonths(startDate) {
  const months = [];
  const [startYear, startMonth] = startDate.slice(0, 7).split('-').map(Number);
  const now = new Date();
  const limitYear = now.getFullYear();
  const limitMonth = now.getMonth() + 1;

  let year = startYear;
  let month = startMonth;

  while (year < limitYear || (year === limitYear && month <= limitMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }

  return [...new Set(months)];
}

export default function Transactions() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Resolve group_id: URL param → user.group_id (admin) → empty
  const resolvedGroupId = searchParams.get('group_id') || user?.group_id || '';

  const [transactions, setTransactions] = useState([]);
  const [groups, setGroups]   = useState([]);
  const [members, setMembers] = useState([]);
  const [group, setGroup]     = useState(null);
  const [groupId, setGroupId] = useState(resolvedGroupId);
  const [filters, setFilters] = useState({ member_id: '', type: '', date_from: '', date_to: '' });
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [loading, setLoading] = useState(!!resolvedGroupId); // start loading if we already have group
  const [tab, setTab]         = useState('pivot'); // 'pivot' | 'monthly' | 'list'
  const [year, setYear]        = useState(new Date().getFullYear());
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 50;
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]       = useState({ member_id: '', amount: '', type: 'Member Contribution', transaction_date: '', notes: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const [changiaTypes, setChangiaTypes]   = useState([]);
  const [changiaTarget, setChangiaTarget] = useState(null); // transaction being reclassified
  const [changiaChoice, setChangiaChoice] = useState('');
  const [changiaCustom, setChangiaCustom] = useState('');
  const [changiaSaving, setChangiaSaving] = useState(false);

  const [showBulkChangia, setShowBulkChangia] = useState(false);
  const [bulkChoice, setBulkChoice]   = useState('');
  const [bulkCustom, setBulkCustom]   = useState('');
  const [bulkAmount, setBulkAmount]   = useState('');
  const [bulkPeriod, setBulkPeriod]   = useState(new Date().toISOString().slice(0, 7));
  const [bulkSaving, setBulkSaving]   = useState(false);
  const [bulkResult, setBulkResult]   = useState(null);
  const bulkSubmittingRef = useRef(false);

  // Load groups dropdown for superadmin
  useEffect(() => {
    if (user?.role === 'superadmin') {
      api.get('/groups').then((r) => setGroups(r.data));
    }
  }, [user]);

  // Load members when group changes; members only see themselves
  useEffect(() => {
    if (!groupId) return;
    api.get(`/members?group_id=${groupId}`).then((r) => {
      if (user?.role === 'member' && user?.member_id) {
        setMembers(r.data.filter((m) => m.id === user.member_id));
      } else {
        setMembers(r.data);
      }
    });
  }, [groupId]);

  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      return;
    }

    api.get(`/groups/${groupId}`).then((r) => setGroup(r.data)).catch(() => setGroup(null));
  }, [groupId]);

  // Load the group's configured changiwa types (Msiba, Harusi, Ugonjwa, n.k.)
  useEffect(() => {
    if (!groupId) { setChangiaTypes([]); return; }
    api.get(`/groups/${groupId}/changiwa-types`).then((r) => setChangiaTypes(r.data)).catch(() => setChangiaTypes([]));
  }, [groupId]);

  const load = () => {
    if (!groupId) { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({
      group_id: groupId,
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    });
    api.get(`/transactions?${params}`)
      .then((r) => setTransactions(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [groupId]);

  const applyFilters = (e) => { e.preventDefault(); load(); };

  const handleDelete = async (id) => {
    if (!confirm('Futa mchango huu?')) return;
    await api.delete(`/transactions/${id}`);
    load();
  };

  const handleToggleChangia = async (t) => {
    if (t.type === 'Changia') {
      if (!confirm('Hii itarudishwa kuwa ada ya kawaida. Endelea?')) return;
      await api.patch(`/transactions/${t.id}`, { type: 'Member Contribution', notes: '' });
      load();
      return;
    }
    setChangiaChoice('');
    setChangiaCustom('');
    setChangiaTarget(t);
  };

  const selectedChangiaType = changiaTypes.find(ct => ct.name === changiaChoice);
  const changiaCutAmount = (selectedChangiaType && +selectedChangiaType.amount > 0)
    ? Math.min(+selectedChangiaType.amount, +(changiaTarget?.amount || 0))
    : +(changiaTarget?.amount || 0);
  const changiaRemainder = +(changiaTarget?.amount || 0) - changiaCutAmount;

  const submitChangia = async (e) => {
    e.preventDefault();
    const reason = changiaChoice === '__other__' ? changiaCustom.trim() : changiaChoice;
    if (!reason) return;
    setChangiaSaving(true);
    try {
      if (changiaRemainder > 0) {
        // Type has a configured amount smaller than the transaction — split it.
        await api.post(`/transactions/${changiaTarget.id}/split-changia`, { amount: changiaCutAmount, reason });
      } else {
        await api.patch(`/transactions/${changiaTarget.id}`, { type: 'Changia', notes: `Changia: ${reason}` });
      }
      setChangiaTarget(null);
      load();
    } finally {
      setChangiaSaving(false);
    }
  };

  const selectedBulkType = changiaTypes.find(ct => ct.name === bulkChoice);
  const openBulkChangia = () => {
    setBulkChoice('');
    setBulkCustom('');
    setBulkAmount('');
    setBulkPeriod(new Date().toISOString().slice(0, 7));
    setBulkResult(null);
    setShowBulkChangia(true);
  };

  const submitBulkChangia = async (e) => {
    e.preventDefault();
    // Guard against double-click/double-submit firing this twice before React
    // re-renders the disabled button — a real bug that double-cut ~24 members.
    if (bulkSubmittingRef.current) return;
    bulkSubmittingRef.current = true;

    const reason = bulkChoice === '__other__' ? bulkCustom.trim() : bulkChoice;
    const amount = bulkChoice === '__other__' ? +bulkAmount : +(selectedBulkType?.amount || 0);
    if (!reason || amount <= 0) { bulkSubmittingRef.current = false; return; }
    setBulkSaving(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/changia-bulk-cut`, {
        amount,
        reason,
        period: bulkPeriod,
        changiwa_type_id: selectedBulkType?.id || null,
      });
      setBulkResult(data);
      load();
    } finally {
      setBulkSaving(false);
      bulkSubmittingRef.current = false;
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/transactions', { ...form, group_id: groupId });
      setShowModal(false);
      setForm({ member_id: '', amount: '', type: 'Member Contribution', transaction_date: '', notes: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || T.trans.errRecord);
    } finally {
      setSaving(false);
    }
  };

  // Scope to the current cycle only — after a group dissolves and restarts,
  // amounts should read from the restart date, not the group's entire history.
  const cycleTransactions = useMemo(() => {
    if (!group?.start_date) return transactions;
    return transactions.filter(t => t.transaction_date >= group.start_date);
  }, [transactions, group]);

  const allocationConfig = useMemo(() => {
    const monthly = parseFloat(group?.monthly_contribution || 0);
    if (!group?.start_date || monthly <= 0) return null;

    // Skip the group's "grace months" (reserve_months) — those months aren't
    // owed, so contributions should start being allocated from the first
    // month that's actually due (e.g. month 8 instead of month 6).
    const graceMonths = +(group.reserve_months || 0);
    const allMonths = generateMonths(group.start_date);

    return {
      monthly,
      months: allMonths.slice(graceMonths),
    };
  }, [group]);

  const allocatedReport = useMemo(() => {
    if (!allocationConfig) return null;

    const contribs = cycleTransactions.filter((t) => t.type !== 'Kiingilio' && t.type !== 'Changia');
    const paidByMember = {};

    for (const t of contribs) {
      const memberId = String(t.member_id);
      paidByMember[memberId] = (paidByMember[memberId] || 0) + parseFloat(t.amount || 0);
    }

    const rows = members
      .map((member) => {
        const totalPaid = paidByMember[String(member.id)] || 0;
        let remaining = totalPaid;
        const months = {};

        for (const month of allocationConfig.months) {
          if (remaining <= 0) {
            months[month] = 0;
            continue;
          }

          const allocated = Math.min(allocationConfig.monthly, remaining);
          months[month] = allocated;
          remaining -= allocated;
        }

        return {
          key: String(member.id),
          id: member.id,
          name: member.name,
          phone: member.phone,
          months,
          totalPaid,
        };
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const colTotals = {};
    for (const month of allocationConfig.months) {
      colTotals[month] = rows.reduce((sum, row) => sum + (row.months[month] || 0), 0);
    }

    const monthlyData = [...allocationConfig.months]
      .map((month) => {
        const monthRows = rows
          .filter((row) => (row.months[month] || 0) > 0)
          .map((row) => ({
            id: `${row.id}-${month}`,
            member_name: row.name,
            member_phone: row.phone,
            amount: row.months[month],
            type: 'Allocated Contribution',
            transaction_date: `${month}-01 00:00:00`,
          }));

        return {
          month,
          transactions: monthRows,
          total: monthRows.reduce((sum, row) => sum + row.amount, 0),
          member_count: monthRows.length,
        };
      })
      .filter((row) => row.total > 0)
      .sort((a, b) => b.month.localeCompare(a.month));

    return {
      months: allocationConfig.months,
      rows,
      colTotals,
      grandTotal: rows.reduce((sum, row) => sum + row.totalPaid, 0),
      monthlyData,
      monthlyAmount: allocationConfig.monthly,
    };
  }, [allocationConfig, members, cycleTransactions]);

  // ── Monthly grouping (real transactions by paid month) ───────────────────
  const monthlyData = useMemo(() => {
    const contribs = cycleTransactions.filter(t => t.type !== 'Kiingilio' && t.type !== 'Changia');
    const map = {};
    for (const t of contribs) {
      const ym = t.transaction_date?.slice(0, 7) || 'unknown';
      if (!map[ym]) map[ym] = { month: ym, transactions: [], total: 0, members: new Set() };
      map[ym].transactions.push(t);
      map[ym].total += parseFloat(t.amount || 0);
      map[ym].members.add(t.member_name);
    }
    return Object.values(map)
      .map(m => ({ ...m, member_count: m.members.size }))
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [cycleTransactions]);

  // ── Pivot table: member × month, allocated like monthly report ───────────
  const pivotData = useMemo(() => {
    if (allocatedReport) {
      return {
        months: allocatedReport.months,
        rows: allocatedReport.rows,
        colTotals: allocatedReport.colTotals,
        grandTotal: allocatedReport.grandTotal,
      };
    }

    const contribs = cycleTransactions.filter(t => t.type !== 'Kiingilio' && t.type !== 'Changia');
    const monthSet = new Set(contribs.map(t => t.transaction_date?.slice(0, 7)).filter(Boolean));
    const months = [...monthSet].sort();
    const memberMap = {};
    for (const t of contribs) {
      // Use member_id first (most unique), then phone, then name as fallback
      const key = t.member_id ? String(t.member_id) : (t.member_phone || t.member_name || '?');
      const ym  = t.transaction_date?.slice(0, 7) || '';
      if (!memberMap[key]) memberMap[key] = { key, name: t.member_name, phone: t.member_phone, months: {}, totalPaid: 0 };
      memberMap[key].months[ym] = (memberMap[key].months[ym] || 0) + parseFloat(t.amount || 0);
      memberMap[key].totalPaid += parseFloat(t.amount || 0);
    }

    const rows = Object.values(memberMap).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const colTotals = {};
    for (const m of months) {
      colTotals[m] = rows.reduce((s, r) => s + (r.months[m] || 0), 0);
    }
    const grandTotal = Object.values(colTotals).reduce((s, v) => s + v, 0);

    return { months, rows, colTotals, grandTotal };
  }, [allocatedReport, cycleTransactions]);

  // ── Year navigation ──────────────────────────────────────────
  const years = useMemo(() => {
    const ys = new Set(cycleTransactions.map(t => +t.transaction_date?.slice(0, 4)).filter(Boolean));
    return [...ys].sort();
  }, [cycleTransactions]);

  // When data loads, jump to the latest year with data
  useEffect(() => {
    if (years.length > 0) setYear(years[years.length - 1]);
  }, [years.join(',')]);

  const yearIdx = years.indexOf(year);

  const filteredTransactions = useMemo(() =>
    cycleTransactions.filter(t => t.transaction_date?.startsWith(year)),
    [cycleTransactions, year]
  );

  // Reset to page 1 whenever the underlying list changes shape
  useEffect(() => { setListPage(1); }, [groupId, year, filters, tab]);

  const listPageCount = Math.max(1, Math.ceil(filteredTransactions.length / LIST_PAGE_SIZE));
  const paginatedTransactions = useMemo(() =>
    filteredTransactions.slice((listPage - 1) * LIST_PAGE_SIZE, listPage * LIST_PAGE_SIZE),
    [filteredTransactions, listPage]
  );

  const filteredMonthlyData = useMemo(() =>
    monthlyData.filter(m => m.month.startsWith(year)),
    [monthlyData, year]
  );

  const filteredPivotData = useMemo(() => {
    const filteredMonths = pivotData.months.filter(m => m.startsWith(year));
    const rows = pivotData.rows.map(row => {
      const monthsFiltered = Object.fromEntries(
        Object.entries(row.months).filter(([m]) => m.startsWith(year))
      );
      const totalPaid = Object.values(monthsFiltered).reduce((s, v) => s + v, 0);
      return { ...row, months: monthsFiltered, totalPaid };
    });
    const colTotals = {};
    for (const m of filteredMonths) {
      colTotals[m] = rows.reduce((s, r) => s + (r.months[m] || 0), 0);
    }
    return { months: filteredMonths, rows, colTotals, grandTotal: rows.reduce((s, r) => s + r.totalPaid, 0) };
  }, [pivotData, year]);
  // ─────────────────────────────────────────────────────────────

  const totalContribs = useMemo(() =>
    filteredTransactions.filter(t => t.type !== 'Kiingilio' && t.type !== 'Changia').reduce((s, t) => s + parseFloat(t.amount || 0), 0),
    [filteredTransactions]
  );
  const totalKiingilio = useMemo(() =>
    filteredTransactions.filter(t => t.type === 'Kiingilio').reduce((s, t) => s + parseFloat(t.amount || 0), 0),
    [filteredTransactions]
  );

  // ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800">{T.trans.title}</h1>
          {years.length > 0 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => yearIdx > 0 && setYear(years[yearIdx - 1])}
                disabled={yearIdx <= 0}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-600 text-lg disabled:opacity-30 hover:bg-gray-100 transition-colors"
              >
                ‹
              </button>
              <span className="text-xl font-bold text-gray-700 min-w-14 text-center">{year}</span>
              <button
                onClick={() => yearIdx < years.length - 1 && setYear(years[yearIdx + 1])}
                disabled={yearIdx >= years.length - 1}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-600 text-lg disabled:opacity-30 hover:bg-gray-100 transition-colors"
              >
                ›
              </button>
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Link to={`/transactions/import${groupId ? `?group_id=${groupId}` : ''}`}
              className="bg-white border border-gray-300 hover:border-green-500 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
              📥 {T.trans.import}
            </Link>
            {groupId && (
              <button onClick={openBulkChangia}
                className="bg-white border border-purple-300 hover:border-purple-500 text-purple-700 px-4 py-2 rounded-lg text-sm font-medium">
                ✂️ Kata Changia kwa Wote
              </button>
            )}
            {groupId && (
              <button onClick={() => setShowModal(true)}
                className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
                {T.trans.addTransaction}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Group selector (superadmin only) */}
      {user?.role === 'superadmin' && (
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none">
          <option value="">{T.common.selectGroupOption}</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}

      {!groupId ? (
        <div className="text-center py-16 text-gray-400">{T.common.selectGroup}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <>
          {allocationConfig && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {T.trans.allocationNote} <strong>{fmt(allocationConfig.monthly)} TZS</strong>.
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-xs text-green-600 font-semibold uppercase mb-1">{T.trans.monthlyFees}</div>
              <div className="text-xl font-bold text-green-800">{fmt(totalContribs)}</div>
              <div className="text-xs text-green-600">TZS</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-xs text-blue-600 font-semibold uppercase mb-1">{T.trans.entryFee}</div>
              <div className="text-xl font-bold text-blue-800">{fmt(totalKiingilio)}</div>
              <div className="text-xs text-blue-600">TZS</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="text-xs text-yellow-700 font-semibold uppercase mb-1">{T.trans.totalAll}</div>
              <div className="text-xl font-bold text-yellow-800">{fmt(totalContribs + totalKiingilio)}</div>
              <div className="text-xs text-yellow-700">TZS</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="text-xs text-purple-600 font-semibold uppercase mb-1">{T.trans.transCount}</div>
              <div className="text-xl font-bold text-purple-800">{filteredTransactions.length}</div>
              <div className="text-xs text-purple-600">{T.trans.records}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button onClick={() => setTab('pivot')}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === 'pivot' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              📊 {T.trans.pivotTab}
            </button>
            <button onClick={() => setTab('monthly')}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === 'monthly' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              📅 {T.trans.monthlyTab}
            </button>
            <button onClick={() => setTab('list')}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === 'list' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              📋 {T.trans.listTab}
            </button>
          </div>

          {/* ── TAB: PIVOT TABLE (member × month) ── */}
          {tab === 'pivot' && <PivotTable data={filteredPivotData} />}

          {/* ── TAB: KWA MWEZI ── */}
          {tab === 'monthly' && (
            <div className="space-y-4">
              {filteredMonthlyData.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">
                  {T.trans.noTransactions}
                </div>
              ) : filteredMonthlyData.map(m => (
                <MonthSection
                  key={m.month}
                  month={m.month}
                  total={m.total}
                  memberCount={m.member_count}
                  transactions={m.transactions}
                  onDelete={handleDelete}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}

          {/* ── TAB: ORODHA ── */}
          {tab === 'list' && (
            <>
              {/* Filters */}
              <form onSubmit={applyFilters} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <select value={filters.member_id} onChange={(e) => setFilters({ ...filters, member_id: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none">
                    <option value="">{T.trans.filterAll}</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none">
                    <option value="">{T.trans.filterAllTypes}</option>
                    <option value="Member Contribution">Member Contribution</option>
                    <option value="App contribution">App contribution</option>
                    <option value="Kiingilio">Kiingilio</option>
                    <option value="Changia">Changia</option>
                  </select>
                  <input type="date" value={filters.date_from} placeholder="Kuanzia"
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none" />
                  <input type="date" value={filters.date_to} placeholder="Hadi"
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none" />
                </div>
                <button type="submit"
                  className="mt-3 bg-green-700 hover:bg-green-800 text-white px-5 py-2 rounded-lg text-sm font-medium">
                  {T.trans.search}
                </button>
              </form>

              <div className="space-y-3">
                <div className="space-y-3 md:hidden">
                  {paginatedTransactions.map((t) => (
                    <div key={t.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-800">{t.member_name}</p>
                          <p className="mt-1 text-xs font-mono text-slate-400">{t.receipt || t.trans_id || '-'}</p>
                        </div>
                        {isAdmin && <button onClick={() => handleDelete(t.id)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600">{T.common.delete}</button>}
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-slate-50 px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Aina</p>
                          <div className="mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              t.type === 'Kiingilio' ? 'bg-blue-50 text-blue-600'
                              : t.type === 'Changia' ? 'bg-purple-50 text-purple-600'
                              : 'bg-green-50 text-green-700'
                            }`}>
                              {t.type}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Kiasi</p>
                          <p className="mt-1 text-sm font-semibold text-emerald-700">{fmt(t.amount)}</p>
                        </div>
                      </div>
                      <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tarehe</p>
                        <p className="mt-1 text-sm text-slate-700">{new Date(t.transaction_date).toLocaleString('sw-TZ')}</p>
                      </div>
                      {isAdmin && (
                        <button onClick={() => handleToggleChangia(t)}
                          className="mt-3 w-full rounded-full bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-600">
                          {t.type === 'Changia' ? '↩ Rudisha kuwa Ada' : '→ Weka kama Changia'}
                        </button>
                      )}
                    </div>
                  ))}
                  {!filteredTransactions.length && <div className="text-center py-8 text-gray-400">{T.trans.noTransactions}</div>}
                </div>

                <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-gray-500 text-xs uppercase">
                        <th className="text-left px-4 py-3">Receipt</th>
                        <th className="text-left px-4 py-3">Mwanachama</th>
                        <th className="text-left px-4 py-3">Aina</th>
                        <th className="text-right px-4 py-3">Kiasi</th>
                        <th className="text-left px-4 py-3">Tarehe</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedTransactions.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-400 text-xs font-mono">{t.receipt || t.trans_id || '-'}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">{t.member_name}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              t.type === 'Kiingilio' ? 'bg-blue-50 text-blue-600'
                              : t.type === 'Changia' ? 'bg-purple-50 text-purple-600'
                              : 'bg-green-50 text-green-700'
                            }`}>
                              {t.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-700">{fmt(t.amount)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">
                            {new Date(t.transaction_date).toLocaleString('sw-TZ')}
                          </td>
                          <td className="px-4 py-3">
                            {isAdmin && (
                              <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                                <button onClick={() => handleToggleChangia(t)}
                                  className="text-purple-500 hover:text-purple-700 text-xs">
                                  {t.type === 'Changia' ? '↩ Rudisha Ada' : '→ Changia'}
                                </button>
                                <button onClick={() => handleDelete(t.id)}
                                  className="text-red-400 hover:text-red-600 text-xs">Futa</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!filteredTransactions.length && (
                        <tr><td colSpan={6} className="text-center py-8 text-gray-400">{T.trans.noTransactions}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {filteredTransactions.length > LIST_PAGE_SIZE && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <button
                    onClick={() => setListPage(p => Math.max(1, p - 1))}
                    disabled={listPage <= 1}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 disabled:opacity-30 hover:bg-gray-50"
                  >
                    ‹ Nyuma
                  </button>
                  <span className="text-xs text-gray-500">
                    Ukurasa {listPage} / {listPageCount} · {filteredTransactions.length} rekodi
                  </span>
                  <button
                    onClick={() => setListPage(p => Math.min(listPageCount, p + 1))}
                    disabled={listPage >= listPageCount}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 disabled:opacity-30 hover:bg-gray-50"
                  >
                    Mbele ›
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{T.trans.recordTransaction}</h2>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.trans.memberName} *</label>
                <select required value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">{T.trans.chooseGroup}</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.phone})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.trans.amount} *</label>
                <input required type="number" min="1" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.trans.type}</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="Member Contribution">Member Contribution</option>
                  <option value="App contribution">App contribution</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.trans.date}</label>
                <input type="datetime-local" value={form.transaction_date}
                  onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.trans.notes}</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60">
                  {saving ? T.trans.saving : T.common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Changia reclassification modal */}
      {changiaTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Weka kama Changia</h2>
            <p className="text-sm text-gray-500 mb-5">
              {changiaTarget.member_name} — {fmt(changiaTarget.amount)} TZS. Chagua ni mchango wa aina gani (inategemea aina za uchangiaji za kikundi hiki).
            </p>
            <form onSubmit={submitChangia} className="space-y-3">
              {changiaTypes.map((ct) => (
                <label key={ct.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                    changiaChoice === ct.name ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                  }`}>
                  <span className="flex items-center gap-3">
                    <input type="radio" name="changia_type" value={ct.name}
                      checked={changiaChoice === ct.name}
                      onChange={(e) => setChangiaChoice(e.target.value)} />
                    <span className="font-medium text-gray-800">{ct.name}</span>
                  </span>
                  {+ct.amount > 0 && (
                    <span className="text-xs text-gray-400">{fmt(ct.amount)} TZS</span>
                  )}
                </label>
              ))}

              <label className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                  changiaChoice === '__other__' ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                }`}>
                <input type="radio" name="changia_type" value="__other__"
                  checked={changiaChoice === '__other__'}
                  onChange={(e) => setChangiaChoice(e.target.value)} />
                <span className="font-medium text-gray-800">Nyingine (andika)</span>
              </label>
              {changiaChoice === '__other__' && (
                <input type="text" required value={changiaCustom}
                  onChange={(e) => setChangiaCustom(e.target.value)}
                  placeholder="Mfano: Harusi ya Fulani"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              )}

              {!changiaTypes.length && (
                <p className="text-xs text-amber-600">
                  Kikundi hiki bado hakina "aina za changiwa" zilizowekwa (Mipangilio → Changiwa). Unaweza kuandika sababu hapo juu kwa sasa.
                </p>
              )}

              {changiaChoice && changiaChoice !== '__other__' && changiaRemainder > 0 && (
                <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2.5 text-xs text-purple-800">
                  Kiasi cha <strong>{fmt(changiaCutAmount)} TZS</strong> kitakatwa kwenda Changia ({changiaChoice}).
                  Baki ya <strong>{fmt(changiaRemainder)} TZS</strong> itabaki kama ada ya kawaida.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setChangiaTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={changiaSaving || !changiaChoice || (changiaChoice === '__other__' && !changiaCustom.trim())}
                  className="flex-1 bg-purple-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60">
                  {changiaSaving ? T.trans.saving : T.common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Changia cut modal */}
      {showBulkChangia && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Kata Changia kwa Wote</h2>
            <p className="text-sm text-gray-500 mb-5">
              Itakata kiasi hiki kutoka kwenye ada ya kila mwanachama mwenye malipo ya kutosha mwezi uliochagua. Wasio na malipo ya kutosha hawataguswa — utawaona chini ili uwaongezee mchango mpya.
            </p>

            {!bulkResult ? (
              <form onSubmit={submitBulkChangia} className="space-y-3">
                {changiaTypes.map((ct) => (
                  <label key={ct.id}
                    className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                      bulkChoice === ct.name ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                    }`}>
                    <span className="flex items-center gap-3">
                      <input type="radio" name="bulk_changia_type" value={ct.name}
                        checked={bulkChoice === ct.name}
                        onChange={(e) => setBulkChoice(e.target.value)} />
                      <span className="font-medium text-gray-800">{ct.name}</span>
                    </span>
                    <span className="text-xs text-gray-400">{fmt(ct.amount)} TZS</span>
                  </label>
                ))}

                <label className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                    bulkChoice === '__other__' ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                  }`}>
                  <input type="radio" name="bulk_changia_type" value="__other__"
                    checked={bulkChoice === '__other__'}
                    onChange={(e) => setBulkChoice(e.target.value)} />
                  <span className="font-medium text-gray-800">Nyingine (andika)</span>
                </label>
                {bulkChoice === '__other__' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" required value={bulkCustom}
                      onChange={(e) => setBulkCustom(e.target.value)}
                      placeholder="Sababu (mfano Harusi)"
                      className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    <input type="number" required min="1" value={bulkAmount}
                      onChange={(e) => setBulkAmount(e.target.value)}
                      placeholder="Kiasi (TZS)"
                      className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mwezi wa kukata</label>
                  <input type="month" required value={bulkPeriod}
                    onChange={(e) => setBulkPeriod(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowBulkChangia(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                    {T.common.cancel}
                  </button>
                  <button type="submit"
                    disabled={bulkSaving || !bulkChoice || (bulkChoice === '__other__' && (!bulkCustom.trim() || !bulkAmount))}
                    className="flex-1 bg-purple-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60">
                    {bulkSaving ? T.trans.saving : 'Kata Sasa'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                  {bulkResult.message}
                </div>

                {bulkResult.event_id && (
                  <Link to={`/changiwa/${bulkResult.event_id}`}
                    className="block rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800 hover:bg-blue-100">
                    📋 Fungua orodha ya kudumu — waliokatwa na wanaosubiri (hii itabaki hata baada ya kufunga dirisha hili, waweza kurudi wakati wowote kuwakata wanaosubiri).
                  </Link>
                )}

                {bulkResult.not_cut.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <h4 className="font-semibold text-amber-800 text-sm mb-2">
                      ⚠️ Hawajakatwa ({bulkResult.not_cut_count}) — waongezee mchango mpya
                    </h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {bulkResult.not_cut.map((m) => (
                        <div key={m.member_id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs">
                          <span className="font-medium text-gray-800">{m.name}</span>
                          <button
                            onClick={() => {
                              setShowBulkChangia(false);
                              setForm({ member_id: String(m.member_id), amount: '', type: 'Member Contribution', transaction_date: '', notes: '' });
                              setShowModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-800 font-medium"
                          >
                            + Ongeza mchango
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => setShowBulkChangia(false)}
                  className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Funga
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pivot Table: member × month ───────────────────────────────
function PivotTable({ data }) {
  const { months, rows, colTotals, grandTotal } = data;

  if (!months.length) {
    return (
      <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">
        Hakuna michango bado
      </div>
    );
  }

  const shortMonth = (ym) => {
    const [y, m] = ym.split('-');
    const names = ['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ago','Sep','Okt','Nov','Des'];
    return `${names[+m - 1]}\n${y.slice(2)}`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <table className="text-sm min-w-max w-full border-collapse">
        <thead>
          {/* Month headers */}
          <tr className="border-b bg-gray-50">
            <th className="text-left px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-44 border-r border-gray-200 z-10">
              Mwanachama
            </th>
            {months.map(ym => (
              <th key={ym} className="px-2 py-3 font-semibold text-gray-600 text-center min-w-24 whitespace-pre-line text-xs leading-tight">
                {shortMonth(ym)}
              </th>
            ))}
            <th className="px-4 py-3 font-bold text-gray-800 text-right min-w-28 border-l border-gray-200 bg-green-50">
              Jumla
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {rows.map(row => {
            const rowTotal = Object.values(row.months).reduce((s, v) => s + v, 0);
            return (
              <tr key={row.key} className="hover:bg-gray-50 group">
                {/* Member name - sticky */}
                <td className="px-4 py-3 font-medium text-gray-800 sticky left-0 bg-white group-hover:bg-gray-50 border-r border-gray-100 z-10">
                  <div>{row.name}</div>
                  <div className="text-xs text-gray-400">{row.phone}</div>
                </td>

                {/* Monthly amounts */}
                {months.map(ym => {
                  const amount = row.months[ym] || 0;
                  return (
                    <td key={ym} className="px-2 py-3 text-center">
                      {amount > 0 ? (
                        <span className="inline-block bg-green-50 border border-green-200 text-green-800 rounded-lg px-2 py-1 text-xs font-semibold">
                          {fmt(amount)}
                        </span>
                      ) : (
                        <span className="text-gray-200 text-base">—</span>
                      )}
                    </td>
                  );
                })}

                {/* Row total */}
                <td className="px-4 py-3 text-right font-bold text-green-700 border-l border-gray-100 bg-green-50">
                  {fmt(rowTotal)}
                </td>
              </tr>
            );
          })}
        </tbody>

        {/* Column totals footer */}
        <tfoot>
          <tr className="border-t-2 border-gray-300 bg-gray-100">
            <td className="px-4 py-3 font-bold text-gray-800 sticky left-0 bg-gray-100 border-r border-gray-200 z-10 text-xs uppercase">
              Jumla ya Mwezi
            </td>
            {months.map(ym => (
              <td key={ym} className="px-2 py-3 text-center">
                <span className="inline-block bg-green-700 text-white rounded-lg px-2 py-1 text-xs font-bold">
                  {fmt(colTotals[ym])}
                </span>
              </td>
            ))}
            <td className="px-4 py-3 text-right font-bold text-green-800 bg-green-100 border-l border-green-200 text-base">
              {fmt(grandTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Monthly section component ──────────────────────────────────
function MonthSection({ month, total, memberCount, transactions, onDelete, isAdmin }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Month header - always visible, clickable to expand */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${total > 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span className="font-semibold text-gray-800">{monthName(month)}</span>
          <span className="text-xs text-gray-400">{transactions.length} malipo · {memberCount} wanachama</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-bold text-green-700 text-lg">{fmt(total)} TZS</span>
          <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
        </div>
      </button>

      {/* Transactions list - collapsible */}
      {open && (
        <div className="border-t border-gray-100">

          {/* ── Mobile: card list ── */}
          <div className="md:hidden divide-y divide-gray-50">
            {transactions
              .slice()
              .sort((a, b) => a.transaction_date?.localeCompare(b.transaction_date))
              .map(t => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-base font-bold">
                    {(t.member_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 text-sm truncate">{t.member_name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {t.type} · {new Date(t.transaction_date).toLocaleDateString('sw-TZ')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-emerald-700 text-sm">{fmt(t.amount)}</span>
                  {isAdmin && (
                    <button onClick={() => onDelete(t.id)}
                      className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-500 hover:bg-rose-100">
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-t border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase">Jumla ya {monthName(month)}</span>
              <span className="font-bold text-emerald-700">{fmt(total)} TZS</span>
            </div>
          </div>

          {/* ── Desktop: table ── */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-xs uppercase">
                <th className="text-left px-5 py-2">Mwanachama</th>
                <th className="text-left px-4 py-2">Aina</th>
                <th className="text-right px-4 py-2">Kiasi</th>
                <th className="text-left px-4 py-2">Tarehe</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {transactions
                .slice()
                .sort((a, b) => a.transaction_date?.localeCompare(b.transaction_date))
                .map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-5 py-2.5 font-medium text-gray-800">{t.member_name}</td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{t.type}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-green-700">{fmt(t.amount)}</td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">
                    {new Date(t.transaction_date).toLocaleDateString('sw-TZ')}
                  </td>
                  <td className="px-4 py-2.5">
                    {isAdmin && <button onClick={() => onDelete(t.id)} className="text-red-400 hover:text-red-600 text-xs">Futa</button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-100 bg-gray-50">
                <td colSpan={2} className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Jumla ya {monthName(month)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-green-700">{fmt(total)} TZS</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
          </div>

        </div>
      )}
    </div>
  );
}
